import { useState, useEffect } from "react";
import { memberPermissionsService } from "@/services/memberPermissions.service";

export interface UsePendingAccessRequestsOptions {
  familyMembers: any[];
  managedIds: string[];
  familyMemberType?: string; // Should be 'fm' for Family Member
  enabled?: boolean;
}

export interface PendingRequestsState {
  pendingRequests: Record<string, boolean>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePendingAccessRequests = ({
  familyMembers,
  managedIds,
  familyMemberType,
  enabled = true,
}: UsePendingAccessRequestsOptions): PendingRequestsState => {
  const [pendingRequests, setPendingRequests] = useState<
    Record<string, boolean>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkPendingRequests = async () => {
    // Add null checks for managedIds
    const safeManagedIds = managedIds || [];

    // Only run for Family Members when enabled (they check for pending access requests)
    if (
      !enabled ||
      familyMemberType !== "fm" ||
      !familyMembers ||
      familyMembers.length === 0
    ) {
      setPendingRequests({});
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Get all member IDs that need to be checked (those without access)
      const targetMemberIds = familyMembers
        .map((member) => member.id || member._id)
        .filter(
          (memberId) => memberId && !safeManagedIds.includes(memberId)
        ) as string[];

      if (targetMemberIds.length === 0) {
        setPendingRequests({});
        return;
      }

      // Use batch API call instead of individual calls
      const batchResults =
        await memberPermissionsService.checkPendingAccessRequestsBatch(
          targetMemberIds
        );

      // Convert the results to the expected format
      const requests: Record<string, boolean> = {};
      targetMemberIds.forEach((memberId) => {
        requests[memberId] = batchResults[memberId]?.hasPendingRequest || false;
      });

      setPendingRequests(requests);
    } catch (error) {
      // Fallback: check each member individually
      const fallbackRequests: Record<string, boolean> = {};
      const targetMemberIds = familyMembers
        .map((member) => member.id || member._id)
        .filter(
          (memberId) => memberId && !safeManagedIds.includes(memberId)
        ) as string[];

      targetMemberIds.forEach((memberId) => {
        fallbackRequests[memberId] = false;
      });
      setPendingRequests(fallbackRequests);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkPendingRequests();
  }, [familyMemberType, familyMembers, managedIds, enabled]);

  const refetch = () => {
    checkPendingRequests();
  };

  return {
    pendingRequests,
    loading,
    error,
    refetch,
  };
};
